/**
 * Recoloration « couleur unique » d'un logo vectoriel : tous les remplissages,
 * contours, dégradés et styles CSS du SVG passent dans la couleur choisie,
 * en restant 100 % vectoriel (l'export PDF/SVG garde des courbes).
 * `fill:none` / `stroke:none` sont préservés (sinon les formes creuses se bouchent).
 */

const KEEP = /^(none|transparent|inherit)$/i;

export function recolorSvg(svgText: string, color: string): string {
	const paintAttr = (s: string, attr: string) =>
		s
			.replace(new RegExp(`(\\s${attr}\\s*=\\s*")([^"]*)(")`, 'gi'), (_m, a, v, b) =>
				KEEP.test(v.trim()) ? a + v + b : a + color + b,
			)
			.replace(new RegExp(`(\\s${attr}\\s*=\\s*')([^']*)(')`, 'gi'), (_m, a, v, b) =>
				KEEP.test(v.trim()) ? a + v + b : a + color + b,
			);
	let out = paintAttr(paintAttr(paintAttr(svgText, 'fill'), 'stroke'), 'stop-color');
	// styles inline + blocs <style> (exports Illustrator/Inkscape : .st0{fill:#…})
	out = out.replace(/(fill|stroke|stop-color)\s*:\s*([^;"'}<]+)/gi, (_m, prop, v) =>
		KEEP.test(v.trim()) ? `${prop}:${v}` : `${prop}:${color}`,
	);
	// formes sans fill explicite : le défaut hérité (noir) devient la couleur choisie
	out = out.replace(/<svg\b([^>]*)>/i, (_m, attrs: string) => {
		const cleaned = attrs.replace(/\s(fill|color)\s*=\s*("[^"]*"|'[^']*')/gi, '');
		return `<svg${cleaned} fill="${color}" color="${color}">`;
	});
	return out;
}

export const svgToDataUrl = (svgText: string): string =>
	`data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgText)))}`;

/**
 * Recoloration d'un logo raster/PDF : toute la silhouette (pixels non transparents)
 * passe dans la couleur choisie, l'alpha (anti-crénelage, transparence) est conservé.
 * NB : si le logo a un fond opaque, retirer d'abord le fond (pipette), sinon tout
 * le rectangle est coloré.
 */
export async function recolorRaster(src: string, color: string): Promise<string> {
	const img = await new Promise<HTMLImageElement>((resolve, reject) => {
		const im = new Image();
		im.onload = () => resolve(im);
		im.onerror = () => reject(new Error('recolor: image illisible'));
		im.src = src;
	});
	const c = document.createElement('canvas');
	c.width = img.naturalWidth;
	c.height = img.naturalHeight;
	const ctx = c.getContext('2d');
	if (!ctx) throw new Error('recolor: canvas indisponible');
	ctx.drawImage(img, 0, 0);
	ctx.globalCompositeOperation = 'source-in';
	ctx.fillStyle = color;
	ctx.fillRect(0, 0, c.width, c.height);
	return c.toDataURL('image/png');
}
